import { MapPin, Phone, Mail } from "lucide-react";
import { ADDRESS_LINE1, ADDRESS_LINE2, EMAIL, FAQS, HOURS, LUNCH_NUDGE, MAPS_EMBED, PHONE_DISPLAY, PHONE_TEL } from "@/data";
import { Reveal, SectionHead } from "@/components/Reveal";
import { BookButton } from "@/components/Buttons";
import { EnquiryForm } from "@/components/EnquiryForm";
import { Accordion } from "@/components/Accordion";

export const ClinicInfo = () => (
  <div data-testid="clinic-address-info" className="flex h-full flex-col rounded-2xl border border-leaf/15 bg-pine p-8 sm:p-10">
    <div className="flex items-start gap-4">
      <MapPin className="mt-1 h-5 w-5 shrink-0 text-leaf" />
      <div>
        <p className="font-serif text-xl text-cream">Glebe Park Dental</p>
        <p className="mt-1 font-sans text-sm text-mist">{ADDRESS_LINE1}</p>
        <p className="font-sans text-sm text-mist">{ADDRESS_LINE2}</p>
      </div>
    </div>
    <a href={PHONE_TEL} className="group mt-8 flex items-center gap-4" data-testid="contact-phone-link">
      <Phone className="h-5 w-5 text-leaf" />
      <span className="font-serif text-2xl text-cream transition-colors group-hover:text-leaf">{PHONE_DISPLAY}</span>
    </a>
    <a href={`mailto:${EMAIL}`} className="group mt-4 flex items-center gap-4" data-testid="contact-email-link">
      <Mail className="h-5 w-5 text-leaf" />
      <span className="font-sans text-sm text-mist transition-colors group-hover:text-cream">{EMAIL}</span>
    </a>
    <div className="mt-8 border-t border-white/10 pt-8">
      <p className="font-mono text-xs tracking-[0.25em] uppercase text-leaf">Opening hours</p>
      <dl className="mt-4 space-y-2.5">
        {HOURS.map((h) => (
          <div key={h.day} className="flex items-center justify-between font-sans text-sm">
            <dt className="text-mist">{h.day}</dt>
            <dd className={h.time === "Closed" ? "text-mist/60" : "text-cream"}>{h.time}</dd>
          </div>
        ))}
      </dl>
    </div>
    <div className="mt-8">
      <BookButton variant="light" testid="contact-book-button" />
      <p data-testid="contact-lunch-nudge" className="mt-4 font-sans text-xs leading-relaxed text-mist/80">{LUNCH_NUDGE}</p>
    </div>
  </div>
);

const Contact = () => (
  <section id="contact" data-testid="contact-hours-section" className="bg-forest py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
      <SectionHead
        overline="Come and see us"
        title={
          <>
            Next to the park,{" "}
            <span className="italic text-stone">a short stroll from the Canberra Centre.</span>
          </>
        }
      />

      <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <ClinicInfo />
        </Reveal>
        <div className="flex flex-col gap-8 lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-2xl border border-leaf/15">
              <iframe
                title="Glebe Park Dental location — 15 Coranderrk Street, Canberra"
                src={MAPS_EMBED}
                className="h-64 w-full grayscale-[0.6] contrast-[1.05] sm:h-72"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <EnquiryForm />
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.1} className="mt-20">
        <div className="mx-auto max-w-3xl">
          <p className="text-center font-mono text-xs tracking-[0.3em] uppercase text-leaf">Good questions</p>
          <h3 className="mt-4 text-center font-serif text-2xl text-cream sm:text-3xl">Things people usually ask us.</h3>
          <div className="mt-10">
            <Accordion items={FAQS} testid="faq-accordion" itemTestid="faq-item" />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default Contact;
